import type { SVGProps } from 'react'

// Monochrome 16-grid glyph icons used in the Discover filter row. Each icon
// renders as `currentColor` so the surrounding chip / button can tint it
// the same way as text — no separate color tokens, no emoji.

type IconProps = Omit<SVGProps<SVGSVGElement>, 'children' | 'viewBox' | 'fill'>

function glyph(d: string, displayName: string, fillRule?: 'evenodd') {
  function Icon({ width = 12, height = 12, ...rest }: IconProps) {
    return (
      <svg
        viewBox="0 0 16 16"
        width={width}
        height={height}
        aria-hidden="true"
        focusable="false"
        {...rest}
      >
        <path fill="currentColor" fillRule={fillRule} d={d} />
      </svg>
    )
  }
  Icon.displayName = displayName
  return Icon
}

// strokeGlyph — companion to `glyph` for multi-path, stroke-based icons on a
// 24-grid (used by the rail nav icons). Inner markup is raw SVG children
// tinted via `currentColor`; same `{width,height}` props as `glyph`.
function strokeGlyph(inner: string, displayName: string) {
  function Icon({ width = 22, height = 22, ...rest }: IconProps) {
    return (
      <svg
        viewBox="0 0 24 24"
        width={width}
        height={height}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
        {...rest}
        dangerouslySetInnerHTML={{ __html: inner }}
      />
    )
  }
  Icon.displayName = displayName
  return Icon
}

export const PinIcon = glyph(
  'M8 1.5a4.5 4.5 0 0 0-4.5 4.5c0 3.2 4 8 4.5 8s4.5-4.8 4.5-8A4.5 4.5 0 0 0 8 1.5Zm0 6.2a1.8 1.8 0 1 1 0-3.6 1.8 1.8 0 0 1 0 3.6Z',
  'PinIcon',
)

export const ListIcon = glyph('M2 3h12v2H2V3Zm0 4h12v2H2V7Zm0 4h12v2H2v-2Z', 'ListIcon')

export const MapIcon = glyph(
  'M5.5 2 1 4v10l4.5-2 5 2L15 12V2l-4.5 2-5-2Zm0 1.6 5 2v8.8l-5-2V3.6Z',
  'MapIcon',
)

export const SortIcon = glyph(
  'M5 2v9l-2-2-1 1 3.5 3.5L9 10l-1-1-2 2V2H5Zm6 12V5l2 2 1-1L10.5 2.5 7 6l1 1 2-2v9h1Z',
  'SortIcon',
)

export const CalendarIcon = glyph(
  'M4 1v1H2.5A1.5 1.5 0 0 0 1 3.5v10A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-10A1.5 1.5 0 0 0 13.5 2H12V1h-1v1H5V1H4Zm-1.5 4h11v8.5h-11V5Z',
  'CalendarIcon',
)

export const ChevronDownIcon = glyph('M3 6l5 5 5-5-1-1-4 4-4-4-1 1Z', 'ChevronDownIcon')

export const ChevronRightIcon = glyph('M6 3l5 5-5 5-1-1 4-4-4-4 1-1Z', 'ChevronRightIcon')

// Rail nav icons (design "B · 简约", stroke-based, 24-grid):
//   发现 / Discover → twin triangle mountains on a ground line
//   我的 / Me        → clean round avatar (head + shoulders)
export const MountainIcon = strokeGlyph(
  '<path d="M3 19h18" /><path d="M5 19l5-9 4 6" /><path d="M12.5 13l2.5-4 4 6" />',
  'MountainIcon',
)

export const UserIcon = strokeGlyph(
  '<circle cx="12" cy="9" r="3" /><path d="M6 19a6 6 0 0 1 12 0" />',
  'UserIcon',
)
